/**
 * Custom API hooks for endpoints not yet in the generated client.
 * Shares auth logic: reads token from localStorage + refreshes via the same
 * getValidToken function that the generated client uses.
 */
import { useQuery, useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { HttpAppError } from "@/lib/app-errors";
import { resolveApiUrl } from "@/lib/api-base-url";
import { tokenManager } from "@/lib/auth";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Certification {
  id: string;
  nombre: string;
  descripcion: string | null;
  requiereFechaOtorgamiento: boolean;
  requiereFechaVencimiento: boolean;
  vigenciaDias: number | null;
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PersonnelCertification {
  id: string;
  personnelId: string;
  certificationId: string;
  fechaObtencion: string | null;
  fechaVencimiento: string | null;
  documentoUrl: string | null;
  notas: string | null;
  activo: boolean;
  certNombre: string;
  certDescripcion: string | null;
  certRequiereFechaOtorgamiento: boolean;
  certRequiereFechaVencimiento: boolean;
}

export interface LocationRequiredCertification {
  id: string;
  locationId: string;
  certificationId: string;
  obligatoria: boolean;
  createdAt: string;
  certNombre: string;
  certDescripcion: string | null;
  certRequiereFechaOtorgamiento: boolean;
  certRequiereFechaVencimiento: boolean;
}

export interface FeatureFlag {
  key: string;
  enabled: boolean;
  descripcion: string | null;
  grupo: string | null;
  rolesEnabled: string[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface Business {
  id: string;
  clientId: string;
  razonSocial: string;
  razonSocialNormalized: string;
  direccion: string | null;
  activo: boolean;
  createdAt: string;
  updatedAt: string;
  client?: {
    id: string;
    razonSocial: string;
  } | null;
}

export interface ClientLite {
  id: string;
  razonSocial: string;
  activo?: boolean;
}

export interface OptionalItem {
  id: string;
  nombre: string;
  codigo: string | null;
  unidad: string;
  descripcion: string | null;
  orden: number;
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

type PaginatedMeta = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export interface FeedbackMetadata {
  app?: "web";
  pageTitle?: string;
  capturedAt?: string;
  currentUrl?: string;
  userAgent?: string;
  language?: string;
  devicePixelRatio?: number;
  captureStatus?: "success" | "fallback" | "failed";
  captureTarget?: "capture-root" | "body" | "none";
  captureError?: string | null;
  viewport?: {
    width: number;
    height: number;
  };
  screen?: {
    width: number;
    height: number;
  };
  screenshotUrl?: string | null;
  screenshotDataUrl?: string | null;
  attachmentUrls?: string[];
  attachments?: string[];
  prioridad?: "alta" | "media" | "baja";
}

export interface UploadPresignRequest {
  filename: string;
  contentType: string;
  folder?: string;
}

export interface UploadPresignResponse {
  uploadUrl: string;
  fileUrl: string;
}

export interface FeedbackItem {
  id: string;
  userId: string;
  tipo: "bug" | "mejora" | "observacion";
  titulo: string | null;
  descripcion: string;
  ruta: string | null;
  metadata: FeedbackMetadata | null;
  sortOrder?: number;
  assignedToUserId: string | null;
  assignedToEmail: string | null;
  assignedToLegajo: string | null;
  assignedToRole: string | null;
  internalNotes: string | null;
  estado: "backlog" | "todo" | "progreso" | "done" | "archivado";
  resolvedAt: string | null;
  reporterEmail: string | null;
  reporterLegajo: string | null;
  reporterRole: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserItem {
  id: string;
  email: string | null;
  legajo: string | null;
  rol: string;
  activo: boolean;
  mustChangePassword: boolean;
  ultimoLogin: string | null;
  personnelId: string | null;
  nombre: string | null;
  apellido: string | null;
  createdAt: string;
}

export interface AuditLogFieldChange {
  field: string;
  label: string;
  previous: string | null;
  next: string | null;
}

export type AuditEntityType = "client" | "service" | "business" | "feedback" | "vehicle" | "personnel" | "location" | "service_type" | "waste_type" | "optional_item" | "trailer_type" | "vehicle_type" | "trailer";

export type AuditAction = "create" | "update" | "delete" | "status_change" | "assign" | "unassign";

export interface AuditLogItem {
  id: string;
  entityType: AuditEntityType;
  entityId: string;
  entityLabel: string | null;
  action: AuditAction;
  summary: string;
  reason: string | null;
  changes: AuditLogFieldChange[] | null;
  metadata: Record<string, unknown> | null;
  snapshot: Record<string, unknown> | null;
  createdBy: string;
  createdAt: string;
  createdByUser: {
    email: string | null;
    legajo: string | null;
    rol: string | null;
    nombre: string | null;
    apellido: string | null;
  };
}

// ─── Reports ──────────────────────────────────────────────────────────────────

export interface ExecutiveDashboard {
  period: { desde: string; hasta: string; days: number };
  servicios: { total: number; completados: number; rechazados: number; cancelados: number; tasaRechazo: number };
  flota: { vehiculosUsados: number; vehiculosTotal: number; utilizacion: number };
  rechazadosPorCliente: { clientId: string; razonSocial: string; rechazados: number }[];
  tendenciaDiaria: { fecha: string; total: number; completados: number; rechazados: number }[];
  alertasVencimiento: {
    personal: { id: string; nombre: string; licVto: string | null; habVto: string | null; examenVto: string | null }[];
    vehiculos: { vehicleId: string; patente: string; tipo: string; proxVto: string | null }[];
    certificaciones: { personnelId: string; nombre: string; certId: string; fechaVencimiento: string | null }[];
  };
}

export type RecurrenceWeekday =
  | "domingo"
  | "lunes"
  | "martes"
  | "miercoles"
  | "jueves"
  | "viernes"
  | "sabado";

export interface CreateRecurringServicesRequest {
  fecha: string;
  clientId: string;
  serviceTypeId: string;
  wasteTypeId?: string | null;
  originId: string;
  destinationId?: string | null;
  tipoFranja?: "fija" | "flexible" | "sin_horario";
  inicioProgramado?: string | null;
  finProgramado?: string | null;
  duracionEstimadaHs?: string | null;
  solicitadoPor?: string | null;
  canalIngreso?: "telefono" | "email" | "whatsapp" | "presencial" | null;
  notasLogistica?: string | null;
  optionals?: Array<{
    optionalItemId: string;
    cantidad: string;
    notas?: string | null;
  }>;
  recurrencia: {
    hasta: string;
    diasSemana: RecurrenceWeekday[];
  };
}

export interface RecurringServiceSummaryItem {
  id: string;
  codigo: string;
  fecha: string;
}

export interface CreateRecurringServicesResponse {
  createdCount: number;
  services: RecurringServiceSummaryItem[];
  recurrencia: {
    desde: string;
    hasta: string;
    diasSemana: RecurrenceWeekday[];
  };
}

// ─── Base fetcher ─────────────────────────────────────────────────────────────

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await tokenManager.getValidToken();
  if (!token) {
    throw new HttpAppError("No autenticado", {
      status: 401,
      code: "UNAUTHORIZED",
    });
  }
  const headers: Record<string, string> = {
    ...(init?.headers as Record<string, string>),
  };
  headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(resolveApiUrl(path), { ...init, credentials: "include", headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let code: string | undefined;
    let message = text || `HTTP ${res.status}`;
    let details: unknown;

    if (text) {
      try {
        const parsed = JSON.parse(text) as { error?: { code?: string; message?: string } };
        code = parsed?.error?.code;
        message = parsed?.error?.message ?? message;
        details = parsed;
      } catch {
        details = text;
      }
    }

    throw new HttpAppError(message, {
      status: res.status,
      code,
      details,
    });
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function createUploadPresign(body: UploadPresignRequest): Promise<UploadPresignResponse> {
  return apiFetch<UploadPresignResponse>("/upload/presign", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ─── Certifications catalog ───────────────────────────────────────────────────

export function useListCertifications() {
  return useQuery({
    queryKey: ["certifications"],
    queryFn: () => apiFetch<{ data: Certification[] }>("/certifications"),
  });
}

export function useCreateCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      nombre: string;
      descripcion?: string | null;
      requiereFechaOtorgamiento?: boolean;
      requiereFechaVencimiento?: boolean;
      vigenciaDias?: number | null;
    }) =>
      apiFetch<{ data: Certification }>("/certifications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["certifications"] }),
  });
}

export function useCreateRecurringServices() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateRecurringServicesRequest) =>
      apiFetch<{ data: CreateRecurringServicesResponse }>("/services/recurring", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/services"] }),
  });
}

export function useListClientsAll(params?: { activo?: boolean; search?: string }) {
  const query = new URLSearchParams();
  if (params?.activo !== undefined) query.set("activo", params.activo ? "true" : "false");
  if (params?.search) query.set("search", params.search);
  query.set("limit", "100");

  return useQuery({
    queryKey: ["clients-all", params?.activo ?? true, params?.search ?? ""],
    queryFn: async () => {
      const all: ClientLite[] = [];
      let page = 1;
      let totalPages = 1;

      do {
        query.set("page", String(page));
        const qs = query.toString();
        const response = await apiFetch<{ data: ClientLite[]; meta?: PaginatedMeta }>(`/clients?${qs}`);
        all.push(...response.data);
        totalPages = response.meta?.totalPages ?? 1;
        page += 1;
      } while (page <= totalPages && page <= 200);

      return { data: all };
    },
  });
}

export interface ListBusinessesPageParams {
  clientId?: string;
  activo?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

/** Una sola página de negocios (con `meta.total`), para `useEntityOptions`. */
export async function fetchBusinessesPage(params: ListBusinessesPageParams = {}) {
  const query = new URLSearchParams();
  if (params.clientId) query.set("clientId", params.clientId);
  if (params.activo !== undefined) query.set("activo", params.activo ? "true" : "false");
  if (params.search) query.set("search", params.search);
  query.set("page", String(params.page ?? 1));
  query.set("limit", String(params.limit ?? 100));
  return apiFetch<{ data: Business[]; meta?: PaginatedMeta }>(`/businesses?${query.toString()}`);
}

export function useListBusinesses(
  params?: { clientId?: string; activo?: boolean; search?: string; limit?: number },
  options?: { query?: { enabled?: boolean } },
) {
  const query = new URLSearchParams();
  if (params?.clientId) query.set("clientId", params.clientId);
  if (params?.activo !== undefined) query.set("activo", params.activo ? "true" : "false");
  if (params?.search) query.set("search", params.search);
  query.set("limit", String(params?.limit ?? 100));

  return useQuery({
    ...(options?.query ?? {}),
    queryKey: ["businesses", params?.clientId ?? "all", params?.activo ?? true, params?.search ?? "", params?.limit ?? 100],
    queryFn: async () => {
      const all: Business[] = [];
      let page = 1;
      let totalPages = 1;

      do {
        query.set("page", String(page));
        const qs = query.toString();
        const response = await apiFetch<{ data: Business[]; meta?: PaginatedMeta }>(`/businesses?${qs}`);
        all.push(...response.data);
        totalPages = response.meta?.totalPages ?? 1;
        page += 1;
      } while (page <= totalPages && page <= 200);

      return { data: all };
    },
  });
}

export function useCreateBusiness() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { clientId: string; razonSocial: string; direccion?: string | null }) => {
      console.log("[api-extra] Creating business", body);
      const response = await apiFetch<{ data: Business }>("/businesses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      console.log("[api-extra] Create business raw response", response);
      return response.data;
    },
    onSuccess: (business) => {
      console.log("[api-extra] Create business success", business);
      qc.invalidateQueries({ queryKey: ["businesses"] });
    },
    onError: (error) => {
      console.error("[api-extra] Create business error", error);
    },
  });
}

export function useUpdateBusiness() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; razonSocial?: string; direccion?: string | null; activo?: boolean }) => {
      console.log("[api-extra] Updating business", { id, ...body });
      const response = await apiFetch<{ data: Business }>(`/businesses/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      console.log("[api-extra] Update business raw response", response);
      return response.data;
    },
    onSuccess: (business) => {
      console.log("[api-extra] Update business success", business);
      qc.invalidateQueries({ queryKey: ["businesses"] });
    },
    onError: (error) => {
      console.error("[api-extra] Update business error", error);
    },
  });
}

export function useDeleteBusiness() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      console.log("[api-extra] Deleting business", id);
      return apiFetch<{ data: Business }>(`/businesses/${id}`, { method: "DELETE" });
    },
    onSuccess: () => {
      console.log("[api-extra] Delete business success");
      qc.invalidateQueries({ queryKey: ["businesses"] });
    },
    onError: (error) => {
      console.error("[api-extra] Delete business error", error);
    },
  });
}

export function useListOptionalItems(params?: { activo?: boolean; search?: string }) {
  const query = new URLSearchParams();
  if (params?.activo !== undefined) query.set("activo", params.activo ? "true" : "false");
  if (params?.search) query.set("search", params.search);
  query.set("limit", "200");

  return useQuery({
    queryKey: ["optional-items", params?.activo ?? true, params?.search ?? ""],
    queryFn: async () => {
      const all: OptionalItem[] = [];
      let page = 1;
      let totalPages = 1;

      do {
        query.set("page", String(page));
        const qs = query.toString();
        const response = await apiFetch<{ data: OptionalItem[]; meta?: PaginatedMeta }>(`/optional-items?${qs}`);
        all.push(...response.data);
        totalPages = response.meta?.totalPages ?? 1;
        page += 1;
      } while (page <= totalPages && page <= 200);

      return { data: all };
    },
  });
}

export function useCreateOptionalItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      nombre: string;
      codigo?: string | null;
      unidad?: string;
      descripcion?: string | null;
      orden?: number;
    }) =>
      apiFetch<OptionalItem>("/optional-items", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["optional-items"] }),
  });
}

export function useUpdateOptionalItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: {
      id: string;
      nombre?: string;
      codigo?: string | null;
      unidad?: string;
      descripcion?: string | null;
      orden?: number;
      activo?: boolean;
    }) =>
      apiFetch<OptionalItem>(`/optional-items/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["optional-items"] }),
  });
}

export function useDeleteOptionalItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/optional-items/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["optional-items"] }),
  });
}

export function useUpdateCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: {
      id: string;
      nombre?: string;
      descripcion?: string | null;
      requiereFechaOtorgamiento?: boolean;
      requiereFechaVencimiento?: boolean;
      vigenciaDias?: number | null;
    }) =>
      apiFetch<{ data: Certification }>(`/certifications/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["certifications"] }),
  });
}

export function useDeleteCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ data: Certification; warnings?: string[] }>(`/certifications/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["certifications"] }),
  });
}

// ─── Personnel certifications ─────────────────────────────────────────────────

export function useListPersonnelCertifications(personnelId: string) {
  return useQuery({
    queryKey: ["personnel-certifications", personnelId],
    queryFn: () => apiFetch<{ data: PersonnelCertification[] }>(`/personnel/${personnelId}/certifications`),
    enabled: !!personnelId,
  });
}

export function useAddPersonnelCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ personnelId, ...body }: {
      personnelId: string; certificationId: string;
      fechaObtencion?: string | null; fechaVencimiento?: string | null; notas?: string | null; documentoUrl?: string | null;
    }) =>
      apiFetch<{ data: PersonnelCertification }>(`/personnel/${personnelId}/certifications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["personnel-certifications", v.personnelId] }),
  });
}

export function useUpdatePersonnelCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ personnelId, certId, ...body }: {
      personnelId: string; certId: string;
      fechaObtencion?: string | null; fechaVencimiento?: string | null; notas?: string | null; documentoUrl?: string | null;
    }) =>
      apiFetch<{ data: PersonnelCertification }>(`/personnel/${personnelId}/certifications/${certId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["personnel-certifications", v.personnelId] }),
  });
}

export function useRemovePersonnelCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ personnelId, certId }: { personnelId: string; certId: string }) =>
      apiFetch<{ data: PersonnelCertification }>(`/personnel/${personnelId}/certifications/${certId}`, { method: "DELETE" }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["personnel-certifications", v.personnelId] }),
  });
}

export interface CertPersonnelStatusItem {
  personnelId: string;
  nombre: string;
  apellido: string;
  legajo: string | null;
  cert: {
    id: string;
    fechaObtencion: string | null;
    fechaVencimiento: string | null;
    notas: string | null;
  } | null;
}

export function useCertPersonnelStatus(certId: string) {
  return useQuery({
    queryKey: ["cert-personnel-status", certId],
    queryFn: () => apiFetch<{ data: CertPersonnelStatusItem[] }>(`/certifications/${certId}/personnel-status`),
    enabled: !!certId,
  });
}

// ─── Location required certifications ────────────────────────────────────────

export function useListLocationCertifications(locationId: string) {
  return useQuery({
    queryKey: ["location-certifications", locationId],
    queryFn: () => apiFetch<{ data: LocationRequiredCertification[] }>(`/locations/${locationId}/required-certifications`),
    enabled: !!locationId,
  });
}

export function useAddLocationCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, certificationId, obligatoria }: { locationId: string; certificationId: string; obligatoria: boolean }) =>
      apiFetch<{ data: LocationRequiredCertification }>(`/locations/${locationId}/required-certifications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ certificationId, obligatoria }),
      }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["location-certifications", v.locationId] }),
  });
}

export function useRemoveLocationCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, certId }: { locationId: string; certId: string }) =>
      apiFetch<{ data: LocationRequiredCertification }>(`/locations/${locationId}/required-certifications/${certId}`, { method: "DELETE" }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["location-certifications", v.locationId] }),
  });
}

// ─── Feature flags ────────────────────────────────────────────────────────────

export function useListFeatureFlags() {
  return useQuery({
    queryKey: ["feature-flags"],
    queryFn: () => apiFetch<{ data: FeatureFlag[] }>("/feature-flags"),
  });
}

export function useUpdateFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, ...body }: {
      key: string;
      enabled?: boolean;
      descripcion?: string | null;
      grupo?: string | null;
      rolesEnabled?: string[] | null;
    }) =>
      apiFetch<{ data: FeatureFlag }>(`/feature-flags/${key}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feature-flags"] }),
  });
}

// ─── Feedback ─────────────────────────────────────────────────────────────────

export function useListFeedback(estado?: string) {
  return useQuery({
    queryKey: ["feedback", estado],
    queryFn: () => apiFetch<{ data: FeedbackItem[] }>(`/feedback${estado ? `?estado=${estado}` : ""}`),
  });
}

export function useCreateFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { tipo: "bug" | "mejora" | "observacion"; titulo?: string; descripcion: string; ruta?: string; metadata?: FeedbackMetadata | null }) =>
      apiFetch<{ data: FeedbackItem }>("/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feedback"] }),
  });
}

export function useUpdateFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: {
      id: string;
      estado?: "backlog" | "todo" | "progreso" | "done" | "archivado";
      titulo?: string | null;
      assignedToUserId?: string | null;
      internalNotes?: string | null;
    }) =>
      apiFetch<{ data: FeedbackItem }>(`/feedback/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feedback"] }),
  });
}

export function useDeleteFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<null>(`/feedback/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feedback"] }),
  });
}

// ─── Users ────────────────────────────────────────────────────────────────────

export function useListUsers() {
  return useQuery({
    queryKey: ["users"],
    queryFn: () => apiFetch<{ data: UserItem[] }>("/users"),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      email?: string | null; legajo?: string | null; password?: string | null; pin?: string | null;
      rol: string; personnelId?: string | null; activo?: boolean;
    }) =>
      apiFetch<{ data: UserItem }>("/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["/api/personnel"] });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: {
      id: string; email?: string | null; legajo?: string | null; rol?: string;
      personnelId?: string | null; activo?: boolean; newPassword?: string; newPin?: string;
    }) =>
      apiFetch<{ data: UserItem }>(`/users/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["/api/personnel"] });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ data: UserItem }>(`/users/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["/api/personnel"] });
    },
  });
}

export interface BlanquearResult {
  id: string;
  tempCredential: string;
  credentialType: "password" | "pin";
}

export function useBlanquearUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ data: BlanquearResult }>(`/users/${id}/blanquear`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useChangeCredential() {
  return useMutation({
    mutationFn: (body: {
      currentPassword?: string;
      newPassword?: string;
      currentPin?: string;
      newPin?: string;
    }) =>
      apiFetch<{ data: { id: string; mustChangePassword: boolean }; accessToken: string }>(
        "/auth/change-credential",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      ),
  });
}

// ─── Audit logs ──────────────────────────────────────────────────────────────

export function useListAuditLogs(filters: {
  entityType?: AuditEntityType;
  action?: AuditAction;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}) {
  const params = new URLSearchParams();
  if (filters.entityType) params.set("entityType", filters.entityType);
  if (filters.action) params.set("action", filters.action);
  if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.set("dateTo", filters.dateTo);
  if (filters.page) params.set("page", String(filters.page));
  if (filters.limit) params.set("limit", String(filters.limit));

  return useQuery({
    queryKey: ["audit-logs", filters],
    queryFn: () => apiFetch<{
      data: AuditLogItem[];
      meta: { page: number; limit: number; total: number; totalPages: number };
    }>(`/audit-logs${params.size > 0 ? `?${params.toString()}` : ""}`),
    staleTime: 30_000,
  });
}

// ─── Executive dashboard ──────────────────────────────────────────────────────

export function useExecutiveDashboard(days = 30) {
  return useQuery({
    queryKey: ["dashboard-executive", days],
    queryFn: () => apiFetch<ExecutiveDashboard>(`/dashboard/executive?days=${days}`),
    staleTime: 60_000,
  });
}

// ─── Reports ──────────────────────────────────────────────────────────────────

export interface AnualReportMonth {
  mes: number;
  label: string;
  serviciosTotal: number;
  serviciosCompletados: number;
  serviciosRechazados: number;
  tasaRechazo: number;
  vehiculosUsados: number;
  vehiculosCompletados?: number;
}

export interface AnualReportTotals {
  serviciosTotal: number;
  serviciosCompletados: number;
  serviciosRechazados: number;
  tasaRechazo: number;
}

export interface AnualReport {
  year: number;
  prevYear: number;
  vehiculosTotal?: number;
  mesesActual: AnualReportMonth[];
  mesesAnterior: AnualReportMonth[];
  totalesActual: AnualReportTotals;
  totalesAnterior: AnualReportTotals;
  comparacion: { variacionServicios: number | null };
}

export interface ServiciosReportRow {
  id: string;
  codigo: string;
  fecha: string;
  estado: string;
  clientId: string | null;
  cliente: string | null;
  origen: string | null;
  destino: string | null;
  tipoServicio: string | null;
  tipoResiduo: string | null;
  vehicleId: string | null;
  vehiculo: string | null;
  chofer: string | null;
  cantidad: number | null;
  unidadCantidad: string | null;
  pesajeOrigenKg: number | null;
  pesajeDestinoKg: number | null;
  nroManifiesto: string | null;
  motivoRechazo: string | null;
  motivoCancelacion: string | null;
  camionSalio: boolean;
  facturable: boolean;
  solicitadoPor: string | null;
  canalIngreso: string | null;
  inicioProgramado: string | null;
  finProgramado: string | null;
}

export interface ServiciosReport {
  filters: { desde: string; hasta: string; clientId: string | null; estado: string | null; serviceTypeId: string | null; vehicleId: string | null };
  summary: { total: number; completados: number; rechazados: number; cancelados: number; reprogramados: number; facturables: number; tasaRechazo: number };
  data: ServiciosReportRow[];
}

export function useReportAnual(year: number) {
  return useQuery({
    queryKey: ["report-anual", year],
    queryFn: () => apiFetch<AnualReport>(`/reports/anual?year=${year}`),
    staleTime: 300_000,
  });
}

export function useReportServicios(filters: {
  desde: string;
  hasta: string;
  clientId?: string;
  estado?: string;
  serviceTypeId?: string;
  vehicleId?: string;
}) {
  const params = new URLSearchParams({ desde: filters.desde, hasta: filters.hasta });
  if (filters.clientId) params.set("clientId", filters.clientId);
  if (filters.estado) params.set("estado", filters.estado);
  if (filters.serviceTypeId) params.set("serviceTypeId", filters.serviceTypeId);
  if (filters.vehicleId) params.set("vehicleId", filters.vehicleId);
  return useQuery({
    queryKey: ["report-servicios", filters],
    queryFn: () => apiFetch<ServiciosReport>(`/reports/servicios?${params.toString()}`),
    staleTime: 60_000,
  });
}

// ─── Negocios report ──────────────────────────────────────────────

export interface NegociosReportRow {
  businessId: string | null;
  clientId: string | null;
  cliente: string | null;
  negocio: string;
  totalServicios: number;
  completados: number;
  rechazados: number;
  cancelados: number;
  tasaRechazo: number;
}

export interface NegociosReport {
  filters: { desde: string; hasta: string; clientId: string | null; businessId: string | null; estado: string | null };
  summary: { totalServicios: number; totalCompletados: number; totalRechazados: number; totalCancelados: number; tasaRechazo: number };
  data: NegociosReportRow[];
}

export function useReportNegocios(filters: {
  desde: string;
  hasta: string;
  clientId?: string;
  businessId?: string;
  estado?: string;
}) {
  const params = new URLSearchParams({ desde: filters.desde, hasta: filters.hasta });
  if (filters.clientId) params.set("clientId", filters.clientId);
  if (filters.businessId) params.set("businessId", filters.businessId);
  if (filters.estado) params.set("estado", filters.estado);
  return useQuery({
    queryKey: ["report-negocios", filters],
    queryFn: () => apiFetch<NegociosReport>(`/reports/negocios?${params.toString()}`),
    staleTime: 60_000,
  });
}

// ─── Fleet utilization ───────────────────────────────────────────

export interface FleetUtilizacionItem {
  periodo: string;
  vehiculosAsignados: number;
  vehiculosCompletados: number;
  utilizacion: number;
  utilizacionEfectiva: number;
}

export interface FleetUtilizacionResponse {
  vehiculosTotal: number;
  data: FleetUtilizacionItem[];
}

export function useFleetUtilization(params: {
  desde: string;
  hasta: string;
  granularity?: "day" | "week" | "month";
}) {
  const qp = new URLSearchParams({ desde: params.desde, hasta: params.hasta });
  if (params.granularity) qp.set("granularity", params.granularity);
  return useQuery({
    queryKey: ["fleet-utilization", params],
    queryFn: () => apiFetch<FleetUtilizacionResponse>(`/reports/utilizacion-flota?${qp.toString()}`),
    staleTime: 120_000,
  });
}

// ─── Role permissions ─────────────────────────────────────────────────────────

export function useRolePermissions() {
  return useQuery({
    queryKey: ["role-permissions"],
    queryFn: () => apiFetch<{ data: Record<string, string[]> }>("/role-permissions"),
    staleTime: 5 * 60_000,
  });
}

export function useUpdateRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ role, permissions }: { role: string; permissions: string[] }) =>
      apiFetch<{ data: { role: string; permissions: string[] } }>(`/role-permissions/${role}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ permissions }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["role-permissions"] }),
  });
}

// ─── Paginación completa ──────────────────────────────────────────────────────

/**
 * Trae TODAS las páginas de un listado paginado. La API capa `limit` a 100 sin avisar,
 * así que pedir `limit: 500` devuelve 100 filas: cuando hace falta la lista entera
 * (mapas de nombres, chequeos globales) hay que recorrer las páginas.
 */
export function useListAllPages<TRow, TParams extends object>(
  key: QueryKey,
  fetcher: (params: TParams & { page: number; limit: number }) => Promise<{ data?: TRow[]; meta?: { totalPages?: number } | null }>,
  params: TParams,
  options?: { enabled?: boolean; staleTime?: number },
) {
  return useQuery({
    queryKey: [...key, "all-pages"],
    enabled: options?.enabled,
    staleTime: options?.staleTime,
    queryFn: async () => {
      const all: TRow[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await fetcher({ ...params, page, limit: 100 });
        all.push(...(response.data ?? []));
        totalPages = response.meta?.totalPages ?? 1;
        page += 1;
      } while (page <= totalPages && page <= 200);
      return { data: all };
    },
  });
}

// ─── Novedades del sistema (changelog) ────────────────────────────────────────

export interface ChangelogState {
  ultimaVersionVista: string | null;
  ultimaVersionPublicada: string | null;
}

export const CHANGELOG_STATE_QUERY_KEY = ["changelog", "state"] as const;

export function useChangelogState() {
  return useQuery({
    queryKey: CHANGELOG_STATE_QUERY_KEY,
    queryFn: () => apiFetch<{ data: ChangelogState }>("/changelog/state"),
    staleTime: 5 * 60_000,
  });
}

export function useMarkChangelogSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: string) =>
      apiFetch<{ data: { ultimaVersionVista: string } }>("/changelog/seen", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ version }),
      }),
    onSuccess: (result) => {
      qc.setQueryData<{ data: ChangelogState }>(CHANGELOG_STATE_QUERY_KEY, (prev) =>
        prev
          ? { data: { ...prev.data, ultimaVersionVista: result.data.ultimaVersionVista } }
          : prev,
      );
    },
  });
}
